import Game from '../../../Game';
import BaseUISystem from '../BaseUISystem';
import PIXI from 'pixi.js';
import { PopupID } from '../../../State/UI/PopupID';
import { DataConflictActions } from '../../../State/Game/DataConflictActions';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import {
    IPlayerDataLocation,
    getRestoreSelection,
    updateLostDataPropertyInChosenSet,
} from 'Utility/DataConflict/DataConflictsHelper';
import { DataResolutionPopupLayout } from 'Layouts/DataResolutionPopupLayout';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import SpriteComponent from 'Component/Render/Sprite';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import BaseMenu from 'System/UI/Menu/BaseMenu';
import TextComponent from 'Component/Render/Text';
import Renderable from 'Component/Renderable';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import DateHelper from 'Utility/DateHelper';
import { isUndefined } from 'Utility/Types';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import { report } from 'report';

const base_path_to_bgs = 'resource/graphics/saved_state_bgs/';

/**
 * Designed for use on native-only; will need tweaks for FBInstant
 */
export default class DataConflictsMenuHandler extends BaseMenu {
    private layout: GLayout<DataResolutionPopupLayout>;
    private timeSortedDataSets: IPlayerDataLocation[];
    private chosenDataSet: IPlayerDataLocation;
    private selectScreen: boolean = false;
    private confirming: boolean = false;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.DATA_CONFLICT_POPUP, uiSystem, false);
    }

    async preload(): Promise<void> {
        await super.preload();
        // Level sprite sheets
    }

    protected onCreate() {
        super.onCreate();

        this.layout = getLayout(DataResolutionPopupLayout);
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        this.layout.ChooseContainer.selectSaveBtn1.on(GameObjectEvent.SELECTED, () => this.selectDataSet(1));
        this.layout.ChooseContainer.selectSaveBtn2.on(GameObjectEvent.SELECTED, () => this.selectDataSet(2));
        this.layout.SelectContainer.CanceBtn.on(GameObjectEvent.SELECTED, () => this.toggleScreens());
        this.layout.SelectContainer.SelectedBtn.on(GameObjectEvent.SELECTED, () => this.confirmedSelectedDataSet());

        applyDefaultButtonAnimation(this.layout.ChooseContainer.selectSaveBtn1);
        applyDefaultButtonAnimation(this.layout.ChooseContainer.selectSaveBtn2);
        applyDefaultButtonAnimation(this.layout.SelectContainer.CanceBtn);
        applyDefaultButtonAnimation(this.layout.SelectContainer.SelectedBtn);
    }

    private confirmedSelectedDataSet(): void {
        if (this.confirming) {
            return;
        }
        this.confirming = true;
        try {
            const restoreSelection = getRestoreSelection(this.timeSortedDataSets, this.chosenDataSet);

            this.chosenDataSet = updateLostDataPropertyInChosenSet(this.timeSortedDataSets, this.chosenDataSet);
            // Save to Facebook/Firebase
            Game.sdk.save(this.chosenDataSet.playerData).then(() => {
                Game.sdk.flushSaveData();
                Game.player.data.playerEndpointData = this.chosenDataSet.playerData;
                DataConflictActions.resolveConflicts({
                    data: this.chosenDataSet.playerData,
                    restoreSelection: restoreSelection,
                    playerDataLocation: this.chosenDataSet.location,
                });
            });
        } catch (error) {
            this.confirming = false;
            report('Failed to confirm data conflict resolution choice.', {
                error,
                once: true,
                sampleRate: 0.1,
                fingerprint: 'OnConfirmedSelectedDataSet:FailedToConfirm',
            });
        }
    }

    private selectDataSet(slot: 1 | 2) {
        this.chosenDataSet = this.timeSortedDataSets[slot - 1];
        this.applyDatasetToSlot(this.chosenDataSet, 3, true);
        this.toggleScreens();
    }

    private toggleScreens() {
        this.selectScreen = !this.selectScreen;
        this.layout.ChooseWrap.getComponent(Renderable).container.visible = !this.selectScreen;
        this.layout.SelectWrap.getComponent(Renderable).container.visible = this.selectScreen;
    }

    async onShow(params: { dataSets: IPlayerDataLocation[] }) {
        super.onShow(params);
        this.timeSortedDataSets = params.dataSets.slice().sort((a, b) => {
            if (!a?.playerData?.data_timestamp) {
                return 0;
            }
            if (!b?.playerData?.data_timestamp) {
                return -1;
            }
            return (
                new Date(a?.playerData?.data_timestamp).getTime() - new Date(b?.playerData?.data_timestamp).getTime()
            );
        });

        const { stars: setAStars, coins: setACoins } = this.getCompareValues(this.timeSortedDataSets[0]);
        const { stars: setBStars, coins: setBCoins } = this.getCompareValues(this.timeSortedDataSets[1]);
        const highlightSetA = setAStars === setBStars ? setACoins >= setBCoins : setAStars >= setBStars;
        this.applyDatasetToSlot(this.timeSortedDataSets[0], 2, highlightSetA);
        this.applyDatasetToSlot(this.timeSortedDataSets[1], 1, !highlightSetA);
    }

    private applyDatasetToSlot(dataset: IPlayerDataLocation, slot: 1 | 2 | 3, highlight: boolean = false): void {
        const { level, stars, coins } = this.getCompareValues(dataset);
        const levelNumber = level === 0 ? 1 : level;
        const layout =
            slot === 1
                ? this.layout.ChooseContainer.SaveState1
                : slot === 2
                ? this.layout.ChooseContainer.SaveState2
                : this.layout.SelectContainer.SelectedSave;

        layout.levelText.getComponent(TextComponent).text = Game.locale
            .getText('Level')
            .replace('[x]', levelNumber.toString());
        layout.coinAmount.getComponent(TextComponent).text = coins.toString();
        layout.starAmount.getComponent(TextComponent).text = stars.toString();

        const date = isUndefined(dataset?.playerData?.data_timestamp)
            ? DateHelper.now
            : new Date(dataset?.playerData?.data_timestamp);

        const formattedDate = date.toLocaleDateString();

        const dataSetIndex = this.timeSortedDataSets.indexOf(dataset);
        const dateText = this.getDataEndPointText(dataSetIndex + 1).replace('[X]', formattedDate);
        layout.descText.getComponent(TextComponent).text = dateText;

        if (slot === 3) {
            const restoreConfirmationTextComponent = this.layout.SelectContainer.restoreConfirmationText.getComponent(
                TextComponent
            );
            restoreConfirmationTextComponent.update({
                text: Game.locale.getText('DataConflictResolutionConfirmationFacebook').replace('[X]', dateText),
            });
        }

        layout.frame.getComponent(SpriteComponent).texture = 'savestate_frame_beige';
        layout.descText.getComponent(TextComponent).style.fill = '#bb3c2f';
        let bg: GameObject<PIXI.Sprite>;
        bg = this.layout.ChooseContainer.SaveState1BG;

        if (highlight) {
            layout.frame.getComponent(SpriteComponent).texture = 'savestate_frame_green';
            layout.descText.getComponent(TextComponent).style.fill = '#406833';
            bg = this.layout.ChooseContainer.SaveState2BG;
        }

        const texName = this.getWorldBGTexture(levelNumber);
        Game.resource.addIfNeeded(texName, base_path_to_bgs + texName);
        Game.resource.load().then(() => {
            bg.getComponent(SpriteComponent).texture = texName;
        });
    }

    private getCompareValues(dataset: IPlayerDataLocation): { level: number; stars: number; coins: number } {
        const starDataParsed: number[] = dataset.playerData?.stars ? JSON.parse(dataset.playerData.stars) : [];
        return {
            level: starDataParsed.length,
            stars: starDataParsed.reduce((a: number, b: number) => a + b, 0),
            coins: dataset.playerData?.coins ?? 0,
        };
    }

    private getWorldBGTexture(_levelNum: number): string {
        return `map${WorldDataHelper.getWorldNumber(_levelNum)}.png`;
    }

    private getDataEndPointText(slot: number) {
        switch (slot) {
            case 1:
                return Game.locale.getText('OldProgressDated');
            case 2:
                return Game.locale.getText('NewProgressDated');
            default:
                return Game.locale.getText('OldProgressDated');
        }
    }

    onDestroy() {
        super.onDestroy();
        this.timeSortedDataSets = [];
    }
}
